import React from 'react';
import './App.css';

const ToolsPage = ({ onNavigateHome }) => {
  // Tools data structure
  const toolsCategories = [
    {
      title: 'Trading, Charting & Screening',
      icon: '📊',
      tools: [
        {
          name: 'TradingView',
          url: 'https://www.tradingview.com/',
          description: 'Advanced charting platform with technical analysis tools'
        },
        {
          name: 'CoinMarketCap',
          url: 'https://coinmarketcap.com/',
          description: 'Crypto market cap rankings and data aggregator'
        },
        {
          name: 'CoinGecko',
          url: 'https://www.coingecko.com/',
          description: 'Cryptocurrency prices, charts, and market data'
        },
        {
          name: 'DexScreener',
          url: 'https://dexscreener.com/',
          description: 'Real-time DEX trading data and charts'
        },
        {
          name: 'DexTools',
          url: 'https://www.dextools.io/app',
          description: 'DEX trading tools and analytics'
        },
        {
          name: 'Winrate.io',
          url: 'https://www.winrate.io/',
          description: 'Trading performance analytics'
        }
      ]
    },
    {
      title: 'On-Chain Analytics & Tools',
      icon: '🔗',
      tools: [
        {
          name: 'Defi Llama',
          url: 'https://defillama.com/',
          description: 'DeFi TVL and protocol analytics'
        },
        {
          name: 'Flipside Crypto',
          url: 'https://flipsidecrypto.xyz/',
          description: 'On-chain data analytics platform'
        },
        {
          name: 'Bubblemaps',
          url: 'https://app.bubblemaps.io/',
          description: 'Blockchain visualization and wallet connections'
        },
        {
          name: 'Token Sniffer',
          url: 'https://tokensniffer.com/',
          description: 'Smart contract security scanner'
        },
        {
          name: 'Arkham Intel',
          url: 'https://intel.arkm.com/',
          description: 'Blockchain intelligence and entity tracking'
        },
        {
          name: 'The Block',
          url: 'https://www.theblock.co/',
          description: 'Crypto news and research'
        },
        {
          name: 'LunarCrush',
          url: 'https://lunarcrush.com/',
          description: 'Social sentiment analytics'
        },
        {
          name: 'Token Unlocks',
          url: 'https://token.unlocks.app/',
          description: 'Token vesting and unlock schedules'
        },
        {
          name: 'Token Terminal',
          url: 'https://tokenterminal.com/explorer/metrics/active-developers',
          description: 'Crypto project fundamentals and metrics'
        },
        {
          name: 'ChainList EVM',
          url: 'https://chainlist.org/',
          description: 'EVM network directory and RPC endpoints'
        },
        {
          name: 'CoinMarketCal',
          url: 'https://coinmarketcal.com/en/',
          description: 'Crypto events calendar'
        },
        {
          name: 'Coinbase App Rank TG',
          url: 'https://t.me/coinbaseappstore/',
          description: 'Coinbase App Store rankings tracker'
        },
        {
          name: 'TVList',
          url: 'https://www.tvlist.xyz/',
          description: 'TradingView watchlist sharing'
        }
      ]
    }
  ];

  const getFavicon = (url) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch {
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-chartguy-dark to-black">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-chartguy-dark/95 backdrop-blur-md border-b border-chartguy-yellow/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={onNavigateHome}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl font-display font-bold text-chartguy-yellow">← Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="section-container">
        {/* Title and Description */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 gradient-text" data-testid="tools-title">
            Chart Guy Tools
          </h1>
          <p className="text-gray-300 text-lg max-w-4xl mx-auto leading-relaxed">
            A curated library of essential resources for chart analysts. From trading platforms to on-chain analytics, 
            these are the tools that help Chart Guys stay informed and make better decisions.
          </p>
        </div>

        {/* Tools Categories */}
        {toolsCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-16">
            {/* Category Header */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-4xl">{category.icon}</span>
              <h2 className="text-3xl font-display font-bold text-chartguy-yellow">
                {category.title}
              </h2>
            </div>

            {/* Tools Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.tools.map((tool, toolIndex) => (
                <a
                  key={toolIndex}
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-chartguy-dark/50 border border-chartguy-yellow/30 rounded-xl p-5 hover:border-chartguy-yellow hover:scale-105 transition-all duration-300 hover:shadow-xl hover:shadow-chartguy-yellow/20 cursor-pointer group"
                  data-testid={`tool-card-${categoryIndex}-${toolIndex}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Favicon */}
                    <div className="flex-shrink-0 w-12 h-12 bg-chartguy-dark border border-chartguy-yellow/30 rounded-lg flex items-center justify-center group-hover:border-chartguy-yellow transition">
                      <img 
                        src={getFavicon(tool.url)}
                        alt={tool.name}
                        className="w-8 h-8"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <span className="text-chartguy-yellow text-xl hidden">🔗</span>
                    </div>

                    {/* Tool Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-display font-bold text-chartguy-yellow mb-1 group-hover:text-chartguy-brown transition">
                        {tool.name}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-2 group-hover:text-gray-300 transition">
                        {tool.description}
                      </p>
                    </div>

                    {/* External Link Icon */}
                    <svg 
                      className="w-5 h-5 text-gray-500 group-hover:text-chartguy-yellow transition flex-shrink-0" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <div className="bg-chartguy-dark/50 border border-chartguy-yellow/30 rounded-xl p-6 max-w-3xl mx-auto">
            <p className="text-gray-400 text-sm">
              <span className="text-chartguy-yellow font-semibold">Note:</span> These tools are community recommendations 
              and not affiliated with the Chart Guy project. Always DYOR and verify sources before making any decisions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolsPage;
